// src/scripts/notifications/coreNotifications.js

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

import {
  onAuthStateChanged,
} from "firebase/auth";

import {
  httpsCallable,
} from "firebase/functions";

import {
  app,
  auth,
  db,
  functions,
} from "@/firebase";

const VAPID_KEY = String(
  process.env.VUE_APP_FIREBASE_VAPID_KEY || ""
).trim();

const registerPushTokenCallable = httpsCallable(
  functions,
  "mainRegisterPushToken"
);

const CLICKED_MODULE_PARAM =
  "coredeckNotificationModule";

const MODULES = {
  ORDERING: "ordering",
  ROI: "roi",
  ARTWORK: "artwork",
  HOLIDAY: "holiday",
};

let installed = false;
let currentUid = "";
let unreadNotifications = [];

let unsubscribeNotifications = null;
let unsubscribeAuth = null;
let unsubscribeForegroundMessage = null;

let messagingInstance = null;
let navigationObserver = null;
let uiFrame = null;

let notificationPromptDismissed = false;

/* =========================================================
   Helpers
   ========================================================= */

function clean(value) {
  return String(value ?? "").trim();
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function vapidConfigured() {
  return Boolean(
    VAPID_KEY &&
      !VAPID_KEY.includes("PASTE_") &&
      !VAPID_KEY.includes("YOUR_")
  );
}

function toast(
  message,
  classes = ""
) {
  const text = clean(message);

  if (!text) {
    return;
  }

  if (window.M?.toast) {
    window.M.toast({
      html: escapeHtml(text),
      classes,
    });

    return;
  }

  console.info(
    "[CoreDeck Notifications]",
    text
  );
}

function browserPermission() {
  if (
    typeof window === "undefined" ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }

  return Notification.permission;
}

/* =========================================================
   Route matching
   ========================================================= */

function moduleFromHref(
  hrefValue
) {
  const href =
    clean(hrefValue);

  if (!href) {
    return "";
  }

  try {
    const url =
      new URL(
        href,
        window.location.origin
      );

    const path =
      url.pathname;

    if (
      /\/ordering\/?$/.test(
        path
      )
    ) {
      return MODULES.ORDERING;
    }

    if (
      /\/event-roi\/?$/.test(
        path
      )
    ) {
      return MODULES.ROI;
    }

    if (
      /\/artwork-workflow\/?$/.test(
        path
      )
    ) {
      return MODULES.ARTWORK;
    }

    if (
      /\/holiday-requests\/?$/.test(
        path
      )
    ) {
      return MODULES.HOLIDAY;
    }
  } catch {
    // no-op
  }

  return "";
}

function clickedNotificationModule() {
  try {
    const url =
      new URL(
        window.location.href
      );

    const module =
      clean(
        url.searchParams.get(
          CLICKED_MODULE_PARAM
        )
      );

    return Object
      .values(MODULES)
      .includes(module)
      ? module
      : "";
  } catch {
    return "";
  }
}

function clearClickedNotificationModule() {
  try {
    const url =
      new URL(
        window.location.href
      );

    if (
      !url.searchParams.has(
        CLICKED_MODULE_PARAM
      )
    ) {
      return;
    }

    url.searchParams.delete(
      CLICKED_MODULE_PARAM
    );

    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    // no-op
  }
}

/* =========================================================
   Notification state
   ========================================================= */

function notificationsForModule(
  module
) {
  return unreadNotifications.filter(
    (notification) =>
      clean(
        notification.module
      ) === module &&
      notification.read !== true
  );
}

function moduleCount(
  module
) {
  return notificationsForModule(
    module
  ).length;
}

function moduleBadgeColour(
  module
) {
  const notifications =
    notificationsForModule(
      module
    );

  if (
    notifications.some(
      (notification) =>
        [
          "rejected",
          "declined",
          "overdue",
        ].includes(
          clean(
            notification.status
          )
        )
    )
  ) {
    return [
      "red",
      "darken-2",
    ];
  }

  if (
    notifications.some(
      (notification) =>
        [
          "amendment",
          "due",
          "attention",
        ].includes(
          clean(
            notification.status
          )
        )
    )
  ) {
    return [
      "orange",
      "darken-2",
    ];
  }

  if (
    notifications.some(
      (notification) =>
        clean(
          notification.status
        ) === "approved"
    )
  ) {
    return [
      "green",
      "darken-2",
    ];
  }

  return [
    "blue-grey",
    "darken-1",
  ];
}

/* =========================================================
   Menu badges
   ========================================================= */

function findBadge(
  anchor,
  module
) {
  const badges =
    Array.from(
      anchor.querySelectorAll(
        `.coredeck-notification-badge[data-coredeck-module="${module}"]`
      )
    );

  badges
    .slice(1)
    .forEach(
      (badge) =>
        badge.remove()
    );

  return badges[0] || null;
}

function findMenuWordSpan(
  anchor
) {
  if (
    !anchor?.classList?.contains(
      "core-side-link"
    )
  ) {
    return null;
  }

  return Array
    .from(
      anchor.children
    )
    .find(
      (element) =>
        element.tagName === "SPAN" &&
        !element.classList.contains(
          "coredeck-notification-badge"
        )
    ) || null;
}

function resetBadgeStyles(
  badge
) {
  badge.removeAttribute(
    "style"
  );

  /*
   * Materialize applies:
   *
   * .sidenav span.badge {
   *   margin-top: 13px;
   * }
   *
   * That is wrong for CoreDeck because our sidebar rows
   * already vertically centre their contents.
   */
  badge.style.setProperty(
    "margin-top",
    "0",
    "important"
  );

  badge.style.setProperty(
    "margin-bottom",
    "0",
    "important"
  );

  badge.style.setProperty(
    "float",
    "none",
    "important"
  );

  badge.style.display =
    "inline-flex";

  badge.style.alignItems =
    "center";

  badge.style.justifyContent =
    "center";

  badge.style.verticalAlign =
    "middle";

  badge.style.position =
    "static";

  badge.style.transform =
    "none";
}

function placeBadge(
  anchor,
  badge
) {
  resetBadgeStyles(
    badge
  );

  /*
   * MAIN SIDEBAR
   *
   * icon | word badge | chevron
   */
  const wordSpan =
    findMenuWordSpan(
      anchor
    );

  if (wordSpan) {
    wordSpan.style.display =
      "flex";

    wordSpan.style.alignItems =
      "center";

    wordSpan.style.gap =
      "8px";

    wordSpan.style.paddingRight =
      "0";

    badge.style.setProperty(
      "margin-left",
      "0",
      "important"
    );

    badge.style.setProperty(
      "margin-right",
      "0",
      "important"
    );

    wordSpan.appendChild(
      badge
    );

    return;
  }

  /*
   * FLYOUT ITEMS
   *
   * Holiday Requests | badge
   */
  if (
    anchor.closest(
      ".core-side-flyout"
    )
  ) {
    badge.style.setProperty(
      "margin-left",
      "8px",
      "important"
    );

    badge.style.setProperty(
      "margin-right",
      "0",
      "important"
    );

    badge.style.flex =
      "0 0 auto";

    badge.style.alignSelf =
      "center";

    anchor.appendChild(
      badge
    );

    return;
  }

  badge.style.setProperty(
    "margin-left",
    "8px",
    "important"
  );

  anchor.appendChild(
    badge
  );
}

function syncBadge(
  anchor,
  module
) {
  if (
    !anchor ||
    !module
  ) {
    return;
  }

  const count =
    moduleCount(
      module
    );

  let badge =
    findBadge(
      anchor,
      module
    );

  if (!count) {
    badge?.remove();

    return;
  }

  if (!badge) {
    badge =
      document.createElement(
        "span"
      );
  }

  badge.className = [
    "new",
    "badge",
    "coredeck-notification-badge",
    ...moduleBadgeColour(
      module
    ),
  ].join(" ");

  badge.dataset
    .coredeckModule =
    module;

    badge.textContent =
      `${count} new`;

    badge.setAttribute(
      "data-badge-caption",
      ""
    );

  placeBadge(
    anchor,
    badge
  );
}

function syncParentBadgeForChildRoute(
  childPathPattern,
  module
) {
  document
    .querySelectorAll(
      ".core-flyout-parent"
    )
    .forEach(
      (parent) => {
        const childLink =
          Array.from(
            parent.querySelectorAll(
              "a[href]"
            )
          ).find(
            (anchor) => {
              try {
                const url =
                  new URL(
                    anchor.getAttribute(
                      "href"
                    ),
                    window.location.origin
                  );

                return childPathPattern.test(
                  url.pathname
                );
              } catch {
                return false;
              }
            }
          );

        if (!childLink) {
          return;
        }

        const trigger =
          parent.querySelector(
            ".core-section-trigger"
          );

        if (trigger) {
          syncBadge(
            trigger,
            module
          );
        }
      }
    );
}

function syncMenuBadges() {
  document
    .querySelectorAll(
      "a[href]"
    )
    .forEach(
      (anchor) => {
        const module =
          moduleFromHref(
            anchor.getAttribute(
              "href"
            )
          );

        if (module) {
          syncBadge(
            anchor,
            module
          );
        }
      }
    );

  syncParentBadgeForChildRoute(
    /\/event-roi\/?$/,
    MODULES.ROI
  );

  syncParentBadgeForChildRoute(
    /\/holiday-requests\/?$/,
    MODULES.HOLIDAY
  );
}

/* =========================================================
   First-login notification permission prompt
   ========================================================= */

function removeNotificationPrompt() {
  document
    .querySelectorAll(
      "[data-coredeck-notification-onboarding]"
    )
    .forEach(
      (element) =>
        element.remove()
    );
}

function showNotificationPrompt() {
  if (
    !currentUid ||
    notificationPromptDismissed ||
    !vapidConfigured() ||
    browserPermission() !== "default"
  ) {
    removeNotificationPrompt();

    return;
  }

  if (
    document.querySelector(
      "[data-coredeck-notification-onboarding]"
    )
  ) {
    return;
  }

  const overlay =
    document.createElement(
      "div"
    );

  overlay.dataset
    .coredeckNotificationOnboarding =
    "true";

  overlay.style.position =
    "fixed";

  overlay.style.inset =
    "0";

  overlay.style.zIndex =
    "10000";

  overlay.style.display =
    "flex";

  overlay.style.alignItems =
    "center";

  overlay.style.justifyContent =
    "center";

  overlay.style.padding =
    "20px";

  overlay.style.background =
    "rgba(0,0,0,0.48)";

  const card =
    document.createElement(
      "div"
    );

  card.className =
    "card white";

  card.style.width =
    "min(520px, 100%)";

  card.style.margin =
    "0";

  card.style.borderRadius =
    "12px";

  card.innerHTML = `
    <div class="card-content">
      <span
        class="card-title"
        style="
          font-weight:700;
          display:flex;
          align-items:center;
          gap:10px;
        "
      >
        <i
          class="
            material-icons
            orange-text
            text-darken-2
          "
        >
          notifications_active
        </i>

        Enable CoreDeck notifications
      </span>

      <p
        style="
          margin-top:12px;
          line-height:1.6;
        "
      >
        Allow notifications so CoreDeck can notify you when another user takes an action relevant to you, such as an order being submitted, approved, rejected or returned for amendment, a holiday request being submitted, or an ROI or artwork update.
      </p>
    </div>

    <div
      class="card-action"
      style="
        display:flex;
        justify-content:flex-end;
        gap:10px;
        flex-wrap:wrap;
      "
    >
      <button
        type="button"
        class="btn-flat"
        data-coredeck-notification-not-now
      >
        Not now
      </button>

      <button
        type="button"
        class="
          btn
          orange
          darken-2
          waves-effect
          waves-light
        "
        data-coredeck-notification-enable
      >
        <i class="material-icons left">
          notifications_active
        </i>

        Enable Notifications
      </button>
    </div>
  `;

  overlay.appendChild(
    card
  );

  document.body.appendChild(
    overlay
  );

  card
    .querySelector(
      "[data-coredeck-notification-not-now]"
    )
    ?.addEventListener(
      "click",
      () => {
        notificationPromptDismissed =
          true;

        overlay.remove();
      }
    );

  card
    .querySelector(
      "[data-coredeck-notification-enable]"
    )
    ?.addEventListener(
      "click",
      async () => {
        await enableDeviceNotifications();

        scheduleUiSync();
      }
    );
}

function syncNotificationPrompt() {
  if (
    currentUid &&
    browserPermission() ===
      "default"
  ) {
    showNotificationPrompt();

    return;
  }

  removeNotificationPrompt();
}

/* =========================================================
   UI
   ========================================================= */

function syncUi() {
  syncMenuBadges();

  syncNotificationPrompt();
}

function scheduleUiSync() {
  if (uiFrame) {
    cancelAnimationFrame(
      uiFrame
    );
  }

  uiFrame =
    requestAnimationFrame(
      () => {
        uiFrame =
          null;

        syncUi();
      }
    );
}

/* =========================================================
   Mark notifications read
   ========================================================= */

async function markModuleNotificationsRead(
  module
) {
  if (
    !currentUid ||
    !module
  ) {
    return;
  }

  const unread =
    notificationsForModule(
      module
    );

  if (!unread.length) {
    return;
  }

  const batch =
    writeBatch(
      db
    );

  unread.forEach(
    (notification) => {
      if (
        !notification.id
      ) {
        return;
      }

      batch.update(
        doc(
          db,
          "users",
          currentUid,
          "notifications",
          notification.id
        ),

        {
          read: true,

          readAt:
            serverTimestamp(),
        }
      );
    }
  );

  try {
    await batch.commit();
  } catch (error) {
    console.error(
      "[CoreDeck Notifications] Failed to mark notifications read:",
      error
    );
  }
}

function handleNavigationClick(
  event
) {
  const anchor =
    event.target
      ?.closest?.(
        "a[href]"
      );

  if (!anchor) {
    return;
  }

  const module =
    moduleFromHref(
      anchor.getAttribute(
        "href"
      )
    );

  if (module) {
    markModuleNotificationsRead(
      module
    );
  }
}

/* =========================================================
   Firestore unread listener
   ========================================================= */

function stopNotificationListener() {
  if (
    unsubscribeNotifications
  ) {
    unsubscribeNotifications();
  }

  unsubscribeNotifications =
    null;

  unreadNotifications =
    [];

  scheduleUiSync();
}

function startNotificationListener(
  uid
) {
  stopNotificationListener();

  if (!uid) {
    return;
  }

  const notificationsQuery =
    query(
      collection(
        db,
        "users",
        uid,
        "notifications"
      ),

      where(
        "read",
        "==",
        false
      )
    );

  unsubscribeNotifications =
    onSnapshot(
      notificationsQuery,

      async (
        snapshot
      ) => {
        unreadNotifications =
          snapshot.docs.map(
            (
              notificationDocument
            ) => ({
              id:
                notificationDocument.id,

              ...notificationDocument.data(),
            })
          );

        scheduleUiSync();

        const clickedModule =
          clickedNotificationModule();

        if (
          clickedModule
        ) {
          await markModuleNotificationsRead(
            clickedModule
          );

          clearClickedNotificationModule();
        }
      },

      (error) => {
        console.error(
          "[CoreDeck Notifications] Notification listener failed:",
          error
        );
      }
    );
}

/* =========================================================
   Firebase Messaging
   ========================================================= */

async function browserSupportsMessaging() {
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

async function getMessagingInstance() {
  if (
    messagingInstance
  ) {
    return messagingInstance;
  }

  if (
    !(
      await browserSupportsMessaging()
    )
  ) {
    return null;
  }

  messagingInstance =
    getMessaging(
      app
    );

  return messagingInstance;
}

/* =========================================================
   Foreground push notifications
   ========================================================= */

function installForegroundListener(
  messaging
) {
  if (
    unsubscribeForegroundMessage
  ) {
    return;
  }

  unsubscribeForegroundMessage =
    onMessage(
      messaging,

      async (
        payload
      ) => {
        const data =
          payload?.data ||
          {};

        const title =
          clean(
            data.title
          ) ||
          "CoreDeck";

        const body =
          clean(
            data.body
          );

        if (
          browserPermission() ===
            "granted" &&
          "serviceWorker" in
            navigator
        ) {
          try {
            const registration =
              await navigator
                .serviceWorker
                .ready;

            await registration
              .showNotification(
                title,
                {
                  body,

                  icon:
                    "/favicon.ico",

                  badge:
                    "/favicon.ico",

                  tag:
                    clean(
                      data.notificationId
                    ) ||
                    `coredeck-${
                      clean(
                        data.module
                      ) ||
                      "notification"
                    }`,

                  renotify:
                    [
                      "rejected",
                      "declined",
                      "overdue",
                      "amendment",
                      "due",
                    ].includes(
                      clean(
                        data.status
                      )
                    ),

                  data: {
                    url:
                      clean(
                        data.url
                      ) ||
                      "/",

                    notificationId:
                      clean(
                        data.notificationId
                      ),

                    module:
                      clean(
                        data.module
                      ),
                  },
                }
              );

            return;
          } catch (error) {
            console.warn(
              "[CoreDeck Notifications] System notification display failed:",
              error
            );
          }
        }

        let classes =
          "green darken-2";

        if (
          [
            "rejected",
            "declined",
            "overdue",
          ].includes(
            clean(
              data.status
            )
          )
        ) {
          classes =
            "red darken-2";
        } else if (
          [
            "amendment",
            "due",
            "attention",
          ].includes(
            clean(
              data.status
            )
          )
        ) {
          classes =
            "orange darken-2";
        }

        toast(
          body
            ? `${title}: ${body}`
            : title,

          classes
        );
      }
    );
}

/* =========================================================
   Browser / FCM registration
   ========================================================= */

async function registerCurrentDevice() {
  if (
    !currentUid ||
    !vapidConfigured() ||
    !(
      "serviceWorker" in
      navigator
    ) ||
    browserPermission() !==
      "granted"
  ) {
    return false;
  }

  const messaging =
    await getMessagingInstance();

  if (!messaging) {
    return false;
  }

  const serviceWorkerRegistration =
    await navigator
      .serviceWorker
      .register(
        "/firebase-messaging-sw.js"
      );

  const readyRegistration =
    serviceWorkerRegistration.active
      ? serviceWorkerRegistration
      : await navigator
          .serviceWorker
          .ready;

  const token =
    await getToken(
      messaging,
      {
        vapidKey:
          VAPID_KEY,

        serviceWorkerRegistration:
          readyRegistration,
      }
    );

  if (!token) {
    return false;
  }

  const registrationResult =
    await registerPushTokenCallable({
      token,

      userAgent:
        navigator.userAgent ||
        "",
    });

  if (
    registrationResult
      ?.data
      ?.ok !== true
  ) {
    throw new Error(
      "CoreDeck did not accept the notification token."
    );
  }

  installForegroundListener(
    messaging
  );

  return true;
}

/* =========================================================
   Browser permission
   ========================================================= */

async function enableDeviceNotifications() {
  if (
    !vapidConfigured()
  ) {
    toast(
      "CoreDeck Web Push has not been configured.",
      "orange darken-2"
    );

    return false;
  }

  if (
    browserPermission() ===
      "unsupported"
  ) {
    toast(
      "This browser does not support notifications.",
      "orange darken-2"
    );

    return false;
  }

  if (
    browserPermission() ===
      "denied"
  ) {
    toast(
      "Notifications are blocked by your browser. Change the site's notification permission in your browser settings.",
      "red darken-2"
    );

    return false;
  }

  let permission =
    browserPermission();

  if (
    permission ===
      "default"
  ) {
    permission =
      await Notification
        .requestPermission();
  }

  if (
    permission !==
      "granted"
  ) {
    removeNotificationPrompt();

    return false;
  }

  try {
    const registered =
      await registerCurrentDevice();

    removeNotificationPrompt();

    if (!registered) {
      toast(
        "Browser permission was granted, but this device could not be registered.",
        "orange darken-2"
      );
    }

    return registered;
  } catch (error) {
    console.error(
      "[CoreDeck Notifications] Device registration failed:",
      error
    );

    toast(
      "CoreDeck could not register this browser for notifications.",
      "red darken-2"
    );

    return false;
  }
}

async function refreshExistingPushRegistration() {
  if (
    browserPermission() !==
      "granted"
  ) {
    return;
  }

  try {
    await registerCurrentDevice();
  } catch (error) {
    console.warn(
      "[CoreDeck Notifications] Existing push registration refresh failed:",
      error?.message ||
        error
    );
  }
}

/* =========================================================
   Authentication
   ========================================================= */

async function startForUser(
  user
) {
  notificationPromptDismissed =
    false;

  currentUid =
    clean(
      user?.uid
    );

  if (!currentUid) {
    return;
  }

  startNotificationListener(
    currentUid
  );

  if (
    browserPermission() ===
      "granted"
  ) {
    await refreshExistingPushRegistration();
  }

  scheduleUiSync();
}

function stopForUser() {
  notificationPromptDismissed =
    false;

  currentUid =
    "";

  removeNotificationPrompt();

  stopNotificationListener();

  if (
    unsubscribeForegroundMessage
  ) {
    unsubscribeForegroundMessage();
  }

  unsubscribeForegroundMessage =
    null;
}

/* =========================================================
   Install
   ========================================================= */

export function installCoreNotifications() {
  if (
    installed
  ) {
    return;
  }

  installed =
    true;

  document.addEventListener(
    "click",
    handleNavigationClick,
    true
  );

  if (
    typeof MutationObserver !==
      "undefined"
  ) {
    navigationObserver =
      new MutationObserver(
        () => {
          scheduleUiSync();
        }
      );

    navigationObserver.observe(
      document.documentElement,
      {
        subtree:
          true,

        childList:
          true,
      }
    );
  }

  unsubscribeAuth =
    onAuthStateChanged(
      auth,

      async (
        user
      ) => {
        if (user) {
          await startForUser(
            user
          );
        } else {
          stopForUser();
        }
      }
    );

  scheduleUiSync();
}

export function uninstallCoreNotifications() {
  if (
    !installed
  ) {
    return;
  }

  installed =
    false;

  document.removeEventListener(
    "click",
    handleNavigationClick,
    true
  );

  navigationObserver
    ?.disconnect();

  navigationObserver =
    null;

  if (
    unsubscribeAuth
  ) {
    unsubscribeAuth();
  }

  unsubscribeAuth =
    null;

  stopForUser();
}

export {
  enableDeviceNotifications,
  markModuleNotificationsRead,
};

installCoreNotifications();